import Button from "../../../shared/components/UI/Button";
import Input from "../../../shared/components/UI/Input";
import Form from "../../../shared/components/UI/Form";

import { SubmitHandler, useForm, Controller } from "react-hook-form";
import Checkbox from "../../../shared/components/UI/Checkbox";
import { useState } from 'react'
import UserAgreementModal from '../../../pages/userAgreement/UserAgreementModal'
import { useNavigate, useSearchParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import authService from "../services/authService";
import { AxiosError } from "axios";
import { displayNoConnection, displayNotify } from "../../../shared/services/Notifications/NotificationServices";
import "../styles/LoginForm.scss";
import { IRegisterForm, RegisterPageState } from "../utils/types";
interface Props {
  registerPageState: RegisterPageState;
  setRegisterPageState: React.Dispatch<React.SetStateAction<RegisterPageState>>;
}

const registerNotificationMap: Record<
  number,
  { title: string; description: string }
> = {
  400: {
    title: "Пользователь существует",
    description: "Логин или email уже заняты.",
  },
  403: {
    title: "Регистрация запрещена",
    description: "Регистрация отключена администрацией.",
  },
  422: {
    title: "Ошибка валидации",
    description: "Некорректные данные. Проверьте форму.",
  },
  500: {
    title: "Ошибка сервера",
    description: "Что-то пошло не так. Попробуйте позже.",
  },
  504: {
    title: "Нет соединения",
    description: "Проверьте интернет или попробуйте позже.",
  }
};

function RegisterForm({ registerPageState, setRegisterPageState }: Props) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate();
  const redirect = searchParams.get('return_to') || '/'
  const { register, handleSubmit, getValues, trigger, control, watch } = useForm<IRegisterForm>({
    mode: "onChange",
  });

  const touchedEmail = watch('email')

  const regM = useMutation({
    mutationFn: (form: IRegisterForm) => {
      const { passwordRepeat, ...payload } = form;
      return authService.regFetch(payload);
    },

    onSuccess: () => {
      displayNotify({
        status: "success",
        title: "Успешно!",
        description: "Регистрация прошла успешно",
      });
      navigate(redirect);
    },

    onError: (error: AxiosError) => {
      try {
        const response = error.response;
        const status = response?.status;
        const data = response?.data as any;
        const detail = data?.detail;


        if (!response) {
          displayNoConnection()
          return;
        }

        if (detail?.includes("CSRF Failed")) {
          displayNotify({
            status: "error",
            title: "Ошибка безопасности",
            description: "CSRF ошибка. Обновите страницу и попробуйте снова.",
          });
          return;
        }

        // Если сервер вернул строку или массив — показать одно уведомление
        if (typeof data === "string" || Array.isArray(data)) {
          const message = registerNotificationMap[status ?? 0];
          const description = Array.isArray(data) ? data.join(" ") : data;
          displayNotify({
            status: "error",
            title: "Неизвестная ошибка",
            description: "Попробуйте снова позже",
          });
          return;
        }

        const keys = Object.keys(data || {});

        if (keys.length === 0) {
          displayNotify({
            status: "error",
            title: "Неизвестная ошибка",
            description: "Попробуйте снова позже",
          });

          return;
        }

      } catch {
        displayNotify({
          status: "error",
          title: "Неизвестная ошибка",
          description: "Попробуйте снова позже.",
        });
      }

    },
  });

  const onSubmit: SubmitHandler<IRegisterForm> = async () => {
    await trigger();
    const values = getValues();

    // Валидация: пользовательское соглашение должно быть принято (ждем полного текста соглашения)
    // if (!values.agreement) {
    //   displayNotify({
    //     status: "error",
    //     title: "Примите пользовательское соглашение",
    //     description: "Для регистрации необходимо принять пользовательское соглашение.",
    //   });
    //   return;
    // }

    if (!values.username) {
      displayNotify({
        status: "error",
        title: "Введите логин",
        description: "Поле логина обязательно",
      });
      return;
    }

    const minLoginLength = 3;
    const maxLoginLength = 40;

    if (
      values.username.length < minLoginLength ||
      values.username.length > maxLoginLength
    ) {
      displayNotify({
        status: "error",
        title: "Некорректный логин",
        description: `Длина логина должна быть от ${minLoginLength} до ${maxLoginLength} символов`,
      });
      return;
    }

    if (!/^[a-zA-Z0-9-_]+$/.test(values.username)) {
      displayNotify({
        status: "error",
        title: "Некорректный логин",
        description:
          "Допустимы только латиница, цифры, дефис и нижнее подчеркивание.\n Запрещены: +, ., /, %, пробел",
      });
      return;
    }

    if (!values.callsign) {
      displayNotify({
        status: "error",
        title: "Введите позывной",
        description: "Поле позывного обязательно",
      });
      return;
    }

    const minCallsignLength = 3;
    const maxCallsignLength = 40;

    if (
      values.callsign.length < minCallsignLength ||
      values.callsign.length > maxCallsignLength
    ) {
      displayNotify({
        status: "error",
        title: "Некорректный позывной",
        description: `Длина позывного должна быть от ${minCallsignLength} до ${maxCallsignLength} символов`,
      });
      return;
    }

    if (!values.email) {
      displayNotify({
        status: "error",
        title: "Введите email",
        description: "Поле email обязательно",
      });
      return;
    }

    if (!/^[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(values.email)) {
      displayNotify({
        status: "error",
        title: "Некорректный email",
        description: 'Email должен содержать символ "@" и быть корректным',
      });
      return;
    }

    if (!values.password) {
      displayNotify({
        status: "error",
        title: "Введите пароль",
        description: "Поле пароля обязательно",
      });
      return;
    }

    if (!/^(?=.*[A-Z])(?=.*\d).{8,}$/.test(values.password)) {
      displayNotify({
        status: "error",
        title: "Слабый пароль",
        description: "Минимум 8 символов, 1 заглавная буква и 1 цифра",
      });
      return;
    }

    if (values.password !== values.passwordRepeat) {
      displayNotify({
        status: "error",
        title: "Пароли не совпадают",
        description: "Убедитесь, что оба пароля совпадают",
      });
      return;
    }

    regM.mutate(values);
  };

  const onLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setRegisterPageState("login");
  };

  const [isAgreementOpen, setAgreementOpen] = useState(false)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isPasswordRepeatVisible, setIsPasswordRepeatVisible] = useState(false)

  const touchedPassword = watch('password')
  const touchedPasswordRepeat = watch('passwordRepeat')

  const eye =
    <img
      src={`/${isPasswordVisible ? 'close' : 'open'}-password.svg`}
      className={`login_input-eye ${touchedPassword ? '' : 'login_input-eye--hidden'}`}
      onClick={() => touchedPassword ? (isPasswordVisible ? setIsPasswordVisible(false) : setIsPasswordVisible(true)) : null}
    />

  const eyeRepeat =
    <img
      src={`/${isPasswordRepeatVisible ? 'close' : 'open'}-password.svg`}
      className={`login_input-eye ${touchedPasswordRepeat ? '' : 'login_input-eye--hidden'}`}
      onClick={() => touchedPasswordRepeat ? (isPasswordRepeatVisible ? setIsPasswordRepeatVisible(false) : setIsPasswordRepeatVisible(true)) : null}
    />

  return (
    <div
      className="login-page"
      style={{ position: "absolute", minHeight: "100vh", overflow: "hidden", backgroundColor: 'rgba(12, 12, 12, 0.5)' }}
    >
      <div />
      <div
        className="login-container"
        style={{ position: "relative", zIndex: 1 }}
      >
        <Form
          noValidate
          className="login_form"
          onSubmit={handleSubmit(onSubmit)}
          style={{
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "1px solid #29292A",
            borderRadius: "20px",
          }}
        >
          <div className="login_header">
            <img className="login_logo" src="/skull.svg" alt="logo" />
            <h2 className="login_title">Доброго дня, комрад!</h2>
            <p className="login_subtitle">Заполните форму регистрации</p>
          </div>

          <div className="login_fields">
            <div className="login_input-wrapper">
              <Input
                type="text"
                placeholder="Логин"
                className="login_input"
                {...register("username")}
              />
            </div>

            <div className="login_input-wrapper">
              <Input
                type="text"
                placeholder="Позывной"
                className="login_input"
                {...register("callsign")}
              />
            </div>

            <div className="login_input-wrapper">
              <Input
                type="email"
                placeholder="Email"
                className="login_input"
                {...register("email")}
              />
            </div>
            {touchedEmail?.toLowerCase().includes('@gmail') && (
              <p className="email_warning">
                Доставка писем на Gmail может работать нестабильно. Рекомендуем использовать другой адрес электронной почты.
              </p>
            )}

            <div className="login_input-wrapper">
              <Input
                type={isPasswordVisible ? "text" : "password"}
                placeholder="Пароль"
                className="login_input"
                rightElement={() => eye}
                {...register("password")}
              />
            </div>

            <div className="login_input-wrapper">
              <Input
                type={isPasswordRepeatVisible ? "text" : "password"}
                placeholder="Повторите пароль"
                className="login_input"
                rightElement={() => eyeRepeat}
                {...register("passwordRepeat")}
              />
            </div>
          </div>

          <div className="login_actions">
            {/* Agreement checkbox (validated on submit) */}
            {/* <div className="login_checkbox-row">
              <Controller
                name="agreement"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <Checkbox
                    label={
                      <>
                        <span style={{ fontSize: 13, color: 'inherit' }}>Принять&nbsp;</span>
                        <button
                          type="button"
                          onClick={() => setAgreementOpen(true)}
                          style={{
                            cursor: 'pointer',
                            background: 'transparent',
                            border: 0,
                            padding: 0,
                            color: 'inherit',
                            textDecoration: 'underline',
                            fontSize: 13,
                          }}
                        >
                          пользовательское соглашение
                        </button>
                      </>
                    }
                    name="agreement"
                    value="agreement"
                    checked={!!field.value}
                    onChange={(e: any) => field.onChange(e.target.checked)}
                    className="darkCheckbox"
                    labelStyle={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 0 }}
                    textColor={'#ccc'}
                  />
                )}
              />
            </div> */}

            <Button
              contraststyle="true"
              type="submit"
              disabled={regM.isPending}
              className="login_submit"
            >
              {regM.isPending ? "Регистрируемся..." : "Зарегистрироваться"}
            </Button>

            <p className="forgot-password">
              Уже есть аккаунт? - <button type="button" onClick={onLoginClick} style={{ background: 'transparent', border: 0, padding: 0, color: 'inherit', textDecoration: 'underline' }}>Войти</button>
            </p>
          </div>
          <UserAgreementModal isOpen={isAgreementOpen} setIsOpen={setAgreementOpen} />
        </Form>
      </div>
    </div>
  );
}

export default RegisterForm;
